var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// orbs/OrbManager.ts
import * as THREE from 'three';
import { Orb } from './Orb';
import SceneManager from '../core/SceneManager';
import { registerScenePolicy } from '../core/ScenePolicies';
import * as TextCreation from '../services/creation/TextCreation';
const JSON_EXPANSION_ANIMATOR_KEY = '__jsonExpansionAnimator';
/**
 * OrbManager curates orb instances regardless of where they are rendered.
 * It should focus on constructing orb groups, registering identity/metadata,
 * and brokering scene attachment so orbs remain portable across worlds and
 * scenes without owning higher-level traversal policies.
 */
export class OrbManager {
    constructor() {
        this.orbs = new Map(); // key: orb.group.uuid
        this.orbArray = [];
        this.orbsByType = new Map();
        this.loadedOrbTypes = new Set();
        this.fetchers = {};
        this.fetcherCache = new Map();
        this.cachedOrbsByType = new Map();
        this.fetcherListeners = new Set();
        //portal stuff
        this.portalCounter = 0;
        this.subscribeToSceneEvents();
    }
    static getInstance() {
        if (!OrbManager.instance)
            OrbManager.instance = new OrbManager();
        return OrbManager.instance;
    }
    // ----------------------------
    // Bridge: SceneManager events
    // ----------------------------
    subscribeToSceneEvents() {
        const sceneManager = SceneManager.getInstance();
        // When an object leaves a scene
        sceneManager.on('objectRemoved', ({ object, disposed }) => {
            const orb = this.orbs.get(object.uuid);
            if (!orb)
                return;
            if (disposed) {
                // The group is being disposed by the scene manager — drop this orb entirely.
                this.dropOrbCompletely(object.uuid);
            }
            else {
                // Non-disposing removal: keep the orb loaded; nothing to do.
                // (Queries like getOrbsInScene() consult SceneManager live.)
            }
        });
        // Optional: clear & delete just provide context; per-object handling above already keeps us consistent.
        sceneManager.on('sceneCleared', () => { });
        sceneManager.on('sceneDeleted', () => { });
    }
    dropOrbCompletely(uuid) {
        var _a;
        const orb = this.orbs.get(uuid);
        if (!orb)
            return;
        const userData = (_a = orb.group) === null || _a === void 0 ? void 0 : _a.userData;
        const maybeAnimator = userData === null || userData === void 0 ? void 0 : userData[JSON_EXPANSION_ANIMATOR_KEY];
        if (maybeAnimator && typeof maybeAnimator.dispose === 'function') {
            try {
                maybeAnimator.dispose();
            }
            catch (error) {
                console.warn('[OrbManager] Failed to dispose orb animator', error);
            }
            delete userData[JSON_EXPANSION_ANIMATOR_KEY];
        }
        orb.disposeWebGLResources();
        // Remove from master registries
        this.orbs.delete(uuid);
        const idx = this.orbArray.findIndex(o => { var _a; return ((_a = o.group) === null || _a === void 0 ? void 0 : _a.uuid) === uuid; });
        if (idx !== -1)
            this.orbArray.splice(idx, 1);
        if (orb.orbType) {
            const liveSet = this.orbsByType.get(orb.orbType);
            if (liveSet) {
                liveSet.delete(orb);
                if (liveSet.size === 0)
                    this.orbsByType.delete(orb.orbType);
            }
            const cacheSet = this.cachedOrbsByType.get(orb.orbType);
            if (cacheSet) {
                cacheSet.delete(orb);
                if (cacheSet.size === 0)
                    this.cachedOrbsByType.delete(orb.orbType);
            }
        }
    }
    // ----------------------------
    // Creation / loading / attach/detach
    // ----------------------------
    loadOrb(orb) {
        if (orb.group && this.orbs.has(orb.group.uuid))
            return;
        orb.createOrb(); // must set orb.group
        if (!orb.group) {
            console.warn('[OrbManager] Orb creation failed.');
            return;
        }
        this.orbs.set(orb.group.uuid, orb);
        this.orbArray.push(orb);
        if (orb.orbType) {
            if (!this.orbsByType.has(orb.orbType))
                this.orbsByType.set(orb.orbType, new Set());
            this.orbsByType.get(orb.orbType).add(orb);
        }
    }
    /** Attach orb to a scene (by name or current). Detach from any previous scene automatically. */
    addOrbToScene(orb, sceneName) {
        var _a;
        if (!orb.group) {
            orb.createOrb();
            if (!orb.group)
                return;
        }
        // Ensure loaded tracking
        if (!this.orbs.has(orb.group.uuid)) {
            this.orbs.set(orb.group.uuid, orb);
            this.orbArray.push(orb);
            if (orb.orbType) {
                if (!this.orbsByType.has(orb.orbType))
                    this.orbsByType.set(orb.orbType, new Set());
                this.orbsByType.get(orb.orbType).add(orb);
            }
        }
        // In addOrbToScene() – call initialisePortal after attaching:
        SceneManager.getInstance().addObject(orb.group, sceneName);
        if (((_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.isPortal) && !orb.portalInitialized) {
            const name = sceneName !== null && sceneName !== void 0 ? sceneName : SceneManager.getInstance().getCurrentSceneName();
            this.initializePortal(orb, name);
        }
    }
    /** Detach orb from scene (by name or everywhere). Optionally dispose its GPU resources. */
    removeOrbFromScene(orb, sceneName, dispose = false) {
        if (!orb.group)
            return;
        SceneManager.getInstance().removeObject(orb.group, sceneName, { dispose });
        // If dispose=true, SceneManager will emit and we’ll drop the orb via listener.
    }
    /** Back-compat: remove by UUID and optional THREE.Scene instance. */
    removeOrb(uuid, scene) {
        const orb = this.orbs.get(uuid);
        if (!orb || !orb.group)
            return false;
        const sm = SceneManager.getInstance();
        if (scene) {
            const sceneName = sm.getSceneNameByInstance(scene);
            sm.removeObject(orb.group, sceneName, { dispose: false });
        }
        else {
            sm.removeObject(orb.group, undefined, { dispose: false });
        }
        return true;
    }
    /** Remove from all scenes and dispose; then forget it from OrbManager. */
    hardRemoveOrb(uuid) {
        const orb = this.orbs.get(uuid);
        if (!orb || !orb.group)
            return false;
        // This will emit objectRemoved(disposed=true) and we will drop it via listener too,
        // but we call dropOrbCompletely here to be synchronous.
        SceneManager.getInstance().removeObject(orb.group, undefined, { dispose: true });
        this.dropOrbCompletely(uuid);
        return true;
    }
    // ----------------------------
    // Queries
    // ----------------------------
    getOrb(uuid) {
        return this.orbs.get(uuid);
    }
    getAllOrbs() {
        return this.orbArray;
    }
    getOrbsByType(type) {
        const set = this.orbsByType.get(type);
        return set ? Array.from(set) : [];
    }
    getUniqueOrbTypes() {
        return new Set(this.orbsByType.keys());
    }
    /** Return orbs attached to a given scene instance by consulting SceneManager live. */
    getOrbsInScene(scene) {
        const sm = SceneManager.getInstance();
        const name = sm.getSceneNameByInstance(scene);
        if (!name)
            return [];
        const objects = sm.getObjects(name);
        const out = [];
        for (const obj of objects) {
            const orb = this.orbs.get(obj.uuid);
            if (orb)
                out.push(orb);
        }
        return out;
    }
    /** Get the scene name that contains this orb’s group, if any. */
    getSceneForOrb(orb) {
        if (!orb.group)
            return undefined;
        const rec = SceneManager.getInstance().findObject(orb.group.uuid);
        return rec === null || rec === void 0 ? void 0 : rec.sceneName;
    }
    // ----------------------------
    // Per-frame updates
    // ----------------------------
    updateOrbMotion() {
        const sm = SceneManager.getInstance();
        const currentSceneName = sm.getCurrentSceneName();
        const objects = sm.getObjects(currentSceneName);
        for (const object of objects) {
            const orb = this.orbs.get(object.uuid);
            if (!orb)
                continue;
            orb.updatePosition();
            orb.updateRotation();
        }
    }
    // ----------------------------
    // Fetchers (optional)
    // ----------------------------
    registerFetcher(name, fetchFn) {
        this.fetchers[name] = fetchFn;
        if (!this.fetcherCache.has(name))
            this.fetcherCache.set(name, new Map());
        this.notifyFetchersChanged();
    }
    getFetcherCount() {
        return Object.keys(this.fetchers).length;
    }
    onFetchersChanged(listener) {
        this.fetcherListeners.add(listener);
        return () => this.fetcherListeners.delete(listener);
    }
    notifyFetchersChanged() {
        if (this.fetcherListeners.size === 0)
            return;
        const count = this.getFetcherCount();
        for (const listener of this.fetcherListeners) {
            try {
                listener(count);
            }
            catch (err) {
                console.warn('[OrbManager] fetcher listener error', err);
            }
        }
    }
    computeCacheKey(args) {
        if (!args)
            return '__noargs__';
        try {
            return JSON.stringify(args);
        }
        catch (_a) {
            return String(args);
        }
    }
    callFetcher(name, args, useCache = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const fn = this.fetchers[name];
            if (!fn) {
                console.warn(`[OrbManager] No fetcher registered: ${name}`);
                return [];
            }
            const key = this.computeCacheKey(args);
            const bucket = this.fetcherCache.get(name);
            if (useCache && bucket.has(key))
                return bucket.get(key);
            const orbs = yield fn(args);
            bucket.set(key, orbs);
            for (const orb of orbs)
                this.loadOrb(orb);
            return orbs;
        });
    }
    getCachedFromFetcher(name, args) {
        var _a, _b;
        const key = this.computeCacheKey(args);
        return (_b = (_a = this.fetcherCache.get(name)) === null || _a === void 0 ? void 0 : _a.get(key)) !== null && _b !== void 0 ? _b : [];
    }
    // Optional type-based caches
    cacheOrbs(orbs, orbType) {
        if (!Array.isArray(orbs)) {
            console.warn(`[OrbManager] Invalid orbs for type "${orbType}"`);
            return;
        }
        for (const orb of orbs) {
            if (!orb.orbType)
                orb.orbType = orbType;
            if (!this.cachedOrbsByType.has(orb.orbType))
                this.cachedOrbsByType.set(orb.orbType, new Set());
            this.cachedOrbsByType.get(orb.orbType).add(orb);
        }
    }
    getCachedOrbsByType(type) {
        const set = this.cachedOrbsByType.get(type);
        return set ? Array.from(set) : [];
    }
    getAllCachedOrbs() {
        return Array.from(this.cachedOrbsByType.values()).flatMap(set => Array.from(set));
    }
    // ----------------------------
    // Interaction helper
    // ----------------------------
    handleOrbClick(clickedObject) {
        const parentGroup = clickedObject.parent;
        if (!parentGroup)
            return 'No parent group for clicked object.';
        const orb = this.getOrb(parentGroup.uuid);
        return orb ? orb.displayJson() : `No orb found - ${parentGroup.uuid}`;
    }
    initializePortal(orb, sourceSceneName) {
        var _a;
        // Skip if already initialised or not a portal
        if (!((_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.isPortal) || orb.portalInitialized)
            return;
        orb.portalInitialized = true;
        // Determine or generate the target scene name
        let targetName = orb.orbConfig.portalTargetSceneName;
        if (!targetName) {
            targetName = `${sourceSceneName}__portal${this.portalCounter++}`;
            orb.orbConfig.portalTargetSceneName = targetName;
        }
        const sm = SceneManager.getInstance();
        let targetScene;
        if (sm.hasScene(targetName)) {
            targetScene = sm.getScene(targetName);
        }
        else {
            targetScene = sm.createScene(targetName);
            // Build a default portal world – tint it based on the outer layer colour
            const outer = orb.getInnermostLayer();
            let colour = 0x6666ff;
            if (outer && outer.material.color) {
                colour = outer.material.color.getHex();
            }
            targetScene.background = new THREE.Color(colour);
            targetScene.fog = new THREE.Fog(colour, 0, 20000);
            targetScene.add(new THREE.HemisphereLight(0xaaaaaa, 0x444444, 0.9));
            //decorateAutoPortalScene(targetScene, colour, targetName); //I hate this
            // Register default movement bounds
            registerScenePolicy(targetName, { radius: 20000 });
        }
        // Build the return portal
        const retCfg = {
            radius: orb.orbConfig.radius,
            layers: orb.orbConfig.layers.map(l => (Object.assign({}, l))),
            isPortal: true,
            portalTargetSceneName: sourceSceneName,
            portalResolution: orb.orbConfig.portalResolution,
            portalCameraFOV: orb.orbConfig.portalCameraFOV,
        };
        const returnOrb = new Orb(retCfg, {}, "Return");
        returnOrb.autoGenerated = true;
        returnOrb.portalInitialized = true;
        returnOrb.setPosition(new THREE.Vector3(0, 1000, 0));
        // Load and attach the return orb
        this.loadOrb(returnOrb);
        this.addOrbToScene(returnOrb, targetName);
        // Track the linkage
        orb.linkedPortalUuid = returnOrb.group.uuid;
        returnOrb.linkedPortalUuid = orb.group.uuid;
    }
}
function decorateAutoPortalScene(scene, baseColour, sceneName) {
    const mainColor = new THREE.Color(baseColour);
    const accent = mainColor.clone().offsetHSL(0.08, 0.1, 0.2);
    const lowAccent = mainColor.clone().offsetHSL(-0.05, -0.05, -0.15);
    const label = TextCreation.createTextSprite(`Portal Pocket: ${sceneName}`, 2.5, new THREE.Vector3(0, 480, 0), undefined, undefined, `#${accent.getHexString()}`);
    scene.add(label);
}
